import React from "react";
import { createStyles } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { OrderStatusCell } from "../../../../components/cells/OrderStatusCellV2";
import { Body } from "../../../../components/Typography";
import { formatTimestamp } from "../../../../utils/date.util";
import { formatEventTypeName, getEventBadgeStatus } from "./helpers";

const DIAMOND_SIZE = 12;

const useStyles = createStyles(() => ({
  entry: {
    display: "flex",
    gap: 9,
  },
  timelineTrack: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    width: DIAMOND_SIZE,
    flexShrink: 0,
    paddingTop: tokens.spacing[1],
  },
  diamond: {
    width: DIAMOND_SIZE,
    height: DIAMOND_SIZE,
    border: `1px solid ${tokens.color.semantic.border.subtle}`,
    transform: "rotate(45deg)",
    flexShrink: 0,
    backgroundColor: tokens.color.semantic.bg.surface,
  },
  line: {
    width: 1,
    flex: 1,
    backgroundColor: tokens.color.semantic.border.subtle,
    marginTop: 2,
  },
  content: {
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[2],
    paddingBottom: tokens.spacing[4],
    minWidth: 0,
  },
  badgeRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[1],
    flexWrap: "wrap" as const,
  },
}));

type OrderEvent = NonNullable<NonNullable<GraphQL.OrderEventsQuery["orderByIdentifier"]>["orderEvents"]>[number];

interface EventLogEntryProps {
  event: OrderEvent;
  isLastEntry: boolean;
  isSuperAdmin: boolean;
}

export const EventLogEntry: React.FC<EventLogEntryProps> = ({ event, isLastEntry, isSuperAdmin }) => {
  const { classes } = useStyles();
  const badgeStatus = getEventBadgeStatus(event.eventType);
  const displayName = formatEventTypeName(event.eventType);
  const timestamp = formatTimestamp(event.eventTime);

  return (
    <div className={classes.entry}>
      <div className={classes.timelineTrack}>
        <div className={classes.diamond} />
        {!isLastEntry && <div className={classes.line} />}
      </div>

      <div className={classes.content}>
        <div className={classes.badgeRow}>
          {badgeStatus && <OrderStatusCell status={badgeStatus} />}
          <Body size="sm">{timestamp}</Body>
        </div>

        {isSuperAdmin && <Body weight="semibold">{displayName}</Body>}
        {event.shortDesc && <Body size="sm">{event.shortDesc}</Body>}
      </div>
    </div>
  );
};
